import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";

import { SubmittalSelectionBadge } from "@/components/semantic-badges";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useCanEdit } from "@/lib/capabilities";
import { cn } from "@/lib/utils";

import type { TableMetadata } from "./types";

interface ExtractedTableListProps {
  tables: TableMetadata[];
  activeTableId?: Id<"specPilotExtractedTables">;
  sourceDocument?: string;
  mergeCountByTable: ReadonlyMap<string, number>;
  onActivate: (tableId: Id<"specPilotExtractedTables">) => void;
  onSelectionChange: (tableId: Id<"specPilotExtractedTables">, selected: boolean) => void;
}

/** Left column of the job page: every extracted table, with its include-in-submittal checkbox. */
export function ExtractedTableList({
  tables,
  activeTableId,
  sourceDocument,
  mergeCountByTable,
  onActivate,
  onSelectionChange,
}: ExtractedTableListProps) {
  const canEdit = useCanEdit();

  return (
    <nav
      aria-label="Extracted tables"
      className="rounded-lg border-2 border-border bg-card lg:flex lg:max-h-full lg:flex-col lg:self-start"
    >
      <div className="space-y-0.5 border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold">Extracted Tables</h2>
        {sourceDocument ? (
          <p className="truncate text-xs text-muted-foreground" title={sourceDocument}>
            {sourceDocument}
          </p>
        ) : null}
      </div>
      <ul className="space-y-0.5 p-1.5 lg:min-h-0 lg:overflow-y-auto">
        {tables.map((table, index) => {
          const tableName = table.name || `Table ${index + 1}`;
          const isActive = table._id === activeTableId;
          const mergeCount = mergeCountByTable.get(table._id) ?? 0;
          return (
            <li
              key={table._id}
              className={cn(
                "flex items-start gap-3 rounded-md border border-transparent px-2.5 py-2 transition-colors hover:bg-muted/60",
                isActive && "border-primary/45 bg-primary/10 hover:bg-primary/10"
              )}
            >
              {canEdit ? (
                <Checkbox
                  className="mt-0.5"
                  checked={table.selectedForSubmittal}
                  onCheckedChange={(checked) => onSelectionChange(table._id, checked === true)}
                  aria-label={`Include ${tableName} in submittal`}
                />
              ) : null}
              <button
                type="button"
                className="min-w-0 flex-1 cursor-pointer space-y-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
                aria-current={isActive ? "true" : undefined}
                onClick={() => onActivate(table._id)}
              >
                <span
                  className={cn(
                    "block truncate text-sm font-medium",
                    !table.selectedForSubmittal && "text-muted-foreground"
                  )}
                >
                  {tableName}
                </span>
                <span className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                  <span>
                    Page {table.pageNumber} · {table.rowCount} rows
                  </span>
                  {mergeCount > 0 ? (
                    <Badge variant="outline" size="sm">
                      {mergeCount} Merged
                    </Badge>
                  ) : null}
                  {table.selectedForSubmittal ? null : <SubmittalSelectionBadge selected={false} />}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
